<template>
  <div
    v-if="isAdmin"
    class="floating-admin-bar"
  >
    <div class="floating-admin-bar-content">
      <!-- Mobile: minimal logout strip -->
      <div class="flex items-center gap-2 lg:hidden">
        <div class="admin-badge admin-badge-sm">
          <span class="text-[10px] font-bold">A</span>
        </div>
        <span class="max-w-[120px] truncate text-[11px] font-medium text-white/90">
          {{ userFirstName }}
        </span>
        <button
          type="button"
          class="admin-bar-logout-mobile"
          @click="logout"
        >
          Wyloguj
        </button>
      </div>

      <!-- Desktop: full admin bar -->
      <div class="hidden items-center gap-4 lg:flex">
        <div class="flex items-center gap-2">
          <div class="admin-badge">
            <span class="text-xs font-bold">A</span>
          </div>
          <span class="text-sm font-semibold text-white">
            {{ userName }}
          </span>
        </div>

        <div class="h-6 w-px bg-white/20" />

        <div class="flex items-center gap-2">
          <Link
            :href="$routes.admin_code_medical_center_configuration({ page: 'homepage' })"
            class="admin-bar-button"
            title="Panel CODE"
          >
            <span class="text-xs font-bold">C</span>
            <span class="hidden md:inline">CODE</span>
          </Link>
          <Link
            :href="$routes.admin_infertility_treatment_clinic_configuration({ page: 'homepage' })"
            class="admin-bar-button"
            title="Panel kliniki niepłodności"
          >
            <icon name="office" class="h-4 w-4 fill-current" />
            <span class="hidden md:inline">KLNP</span>
          </Link>
          <Link
            :href="$routes.admin_institute_configuration({ page: 'homepage' })"
            class="admin-bar-button"
            title="Panel instytutu"
          >
            <icon name="book" class="h-4 w-4 fill-current" />
            <span class="hidden md:inline">Instytut</span>
          </Link>
          <Link
            :href="$routes.admin_family_clinic_configuration({ page: 'homepage' })"
            class="admin-bar-button"
            title="Panel kliniki rodziny"
          >
            <icon name="users" class="h-4 w-4 fill-current" />
            <span class="hidden md:inline">KZR</span>
          </Link>
        </div>

        <div class="h-6 w-px bg-white/20" />

        <Link
          :href="$routes.admin_index()"
          class="admin-bar-button-primary"
          title="Główny panel administracyjny"
        >
          <icon name="dashboard" class="h-4 w-4 fill-current" />
          <span class="hidden sm:inline">Panel</span>
        </Link>
      </div>
    </div>
  </div>
</template>

<script>
import { Link } from '@inertiajs/vue3';
import Icon from '@/Shared/Icon.vue';

export default {
  components: {
    Link,
    Icon,
  },
  computed: {
    isAdmin() {
      return this.$page.props.auth?.user != null;
    },
    userName() {
      const user = this.$page.props.auth?.user;
      if (!user) return '';
      return `${user.first_name} ${user.last_name}`;
    },
    userFirstName() {
      return this.$page.props.auth?.user?.first_name || 'Admin';
    },
  },
  methods: {
    logout() {
      this.$inertia.delete(this.$routes.destroy_user_session());
    },
  },
};
</script>

<style scoped>
.floating-admin-bar {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    transform: translateX(-50%) translateY(100px);
    opacity: 0;
  }
  to {
    transform: translateX(-50%) translateY(0);
    opacity: 1;
  }
}

.floating-admin-bar-content {
  background: linear-gradient(
    135deg,
    color-mix(in srgb, var(--color-code-medical-center) 85%, white) 0%,
    var(--color-code-medical-center) 100%
  );
  border-radius: 12px;
  padding: 12px 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3),
              0 2px 8px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.admin-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  color: white;
}

.admin-badge-sm {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.admin-bar-logout-mobile {
  margin-left: 2px;
  border: 0;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.18);
  padding: 4px 8px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  color: white;
  white-space: nowrap;
}

.admin-bar-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s ease;
  text-decoration: none;
  border: 1px solid transparent;
}

.admin-bar-button:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.admin-bar-button:active {
  transform: translateY(0);
}

.admin-bar-button-primary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.95);
  color: var(--color-code-medical-center);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s ease;
  text-decoration: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.admin-bar-button-primary:hover {
  background: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}

.admin-bar-button-primary:active {
  transform: translateY(0);
}

@media (max-width: 1023px) {
  .floating-admin-bar {
    bottom: 12px;
  }

  .floating-admin-bar-content {
    padding: 6px 10px;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25),
                0 1px 4px rgba(0, 0, 0, 0.15);
  }
}
</style>
